import { cva } from "class-variance-authority";

const removedShadowColors = [
  "brand/soft",
  "zinc/soft",
  "indigo/soft",
  "cyan/soft",
  "red/soft",
  "orange/soft",
  "amber/soft",
  "yellow/soft",
  "lime/soft",
  "green/soft",
  "emerald/soft",
  "teal/soft",
  "sky/soft",
  "blue/soft",
  "violet/soft",
  "purple/soft",
  "fuchsia/soft",
  "pink/soft",
  "rose/soft",
] as const;

export const buttonVariants = cva(
  [
    // Base
    "relative isolate inline-flex items-center justify-center border rounded-[--border-radius] font-medium transition-colors",

    // Focus
    "focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500",

    // Disabled
    "disabled:opacity-35 data-[disabled]:opacity-35 data-[disabled]:cursor-default data-[disabled]:pointer-events-none",

    // Icon
    "[&>[data-slot=icon]]:-mx-0.5 [&>[data-slot=icon]]:my-0.5 [&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:sm:my-1 [&>[data-slot=icon]]:sm:size-4",
    "[&>[data-slot=loader]]:-mx-0.5 [&>[data-slot=loader]]:my-0.5 [&>[data-slot=loader]]:size-5 [&>[data-slot=loader]]:shrink-0 [&>[data-slot=loader]]:sm:my-1 [&>[data-slot=loader]]:sm:size-4",
  ],
  {
    defaultVariants: {
      color: "dark/zinc",
      icon: true,
      size: "md",
      variant: "primary",
    },
    variants: {
      color: {
        "dark/zinc": [
          "text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]",
          "dark:text-white dark:[--btn-bg:theme(colors.zinc.600)] dark:[--btn-hover-overlay:theme(colors.white/5%)]",
          "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]",
        ],
        light: [
          "text-zinc-950 [--btn-bg:white] [--btn-border:theme(colors.zinc.950/10%)] [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] data-[active]:[--btn-border:theme(colors.zinc.950/15%)] data-[hover]:[--btn-border:theme(colors.zinc.950/15%)]",
          "dark:text-white dark:[--btn-hover-overlay:theme(colors.white/5%)] dark:[--btn-bg:theme(colors.zinc.800)]",
          "[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)] dark:[--btn-icon:theme(colors.zinc.500)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]",
        ],
        "dark/white": [
          "text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]",
          "dark:text-zinc-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:theme(colors.zinc.950/5%)]",
          "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)] dark:[--btn-icon:theme(colors.zinc.500)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]",
        ],
        dark: [
          "text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]",
          "dark:[--btn-hover-overlay:theme(colors.white/5%)] dark:[--btn-bg:theme(colors.zinc.800)]",
          "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]",
        ],
        white: [
          "text-zinc-950 [--btn-bg:white] [--btn-border:theme(colors.zinc.950/10%)] [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] data-[active]:[--btn-border:theme(colors.zinc.950/15%)] data-[hover]:[--btn-border:theme(colors.zinc.950/15%)]",
          "dark:[--btn-hover-overlay:theme(colors.zinc.950/5%)]",
          "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.500)] data-[hover]:[--btn-icon:theme(colors.zinc.500)]",
        ],
        brand: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.brand.700)] [--btn-border:theme(colors.brand.700)]",
          "[--btn-icon:theme(colors.brand.300)] data-[active]:[--btn-icon:theme(colors.brand.200)] data-[hover]:[--btn-icon:theme(colors.brand.200)]",
        ],
        "brand/soft": [
          "text-brand-700 [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.brand.100)]",
          "[--btn-icon:theme(colors.brand.500)] data-[active]:[--btn-icon:theme(colors.brand.600)] data-[hover]:[--btn-icon:theme(colors.brand.600)]",
        ],
        zinc: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.zinc.600)] [--btn-border:theme(colors.zinc.700/90%)]",
          "dark:[--btn-hover-overlay:theme(colors.white/5%)]",
          "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]",
        ],
        "zinc/soft": [
          "text-zinc-700 [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] [--btn-bg:theme(colors.zinc.100)]",
          "[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.600)] data-[hover]:[--btn-icon:theme(colors.zinc.600)]",
        ],
        indigo: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.indigo.500)] [--btn-border:theme(colors.indigo.600/90%)]",
          "[--btn-icon:theme(colors.indigo.300)] data-[active]:[--btn-icon:theme(colors.indigo.200)] data-[hover]:[--btn-icon:theme(colors.indigo.200)]",
        ],
        "indigo/soft": [
          "text-indigo-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.indigo.100)]",
          "[--btn-icon:theme(colors.indigo.600)] data-[active]:[--btn-icon:theme(colors.indigo.700)] data-[hover]:[--btn-icon:theme(colors.indigo.700)]",
        ],
        cyan: [
          "text-cyan-950 [--btn-bg:theme(colors.cyan.300)] [--btn-border:theme(colors.cyan.400/80%)] [--btn-hover-overlay:theme(colors.white/25%)]",
          "[--btn-icon:theme(colors.cyan.500)]",
        ],
        "cyan/soft": [
          "text-cyan-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.cyan.100)]",
          "[--btn-icon:theme(colors.cyan.500)] data-[active]:[--btn-icon:theme(colors.cyan.600)] data-[hover]:[--btn-icon:theme(colors.cyan.600)]",
        ],
        red: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.red.600)] [--btn-border:theme(colors.red.700/90%)]",
          "[--btn-icon:theme(colors.red.300)] data-[active]:[--btn-icon:theme(colors.red.200)] data-[hover]:[--btn-icon:theme(colors.red.200)]",
        ],
        "red/soft": [
          "text-red-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.red.100)]",
          "[--btn-icon:theme(colors.red.600)] data-[active]:[--btn-icon:theme(colors.red.700)] data-[hover]:[--btn-icon:theme(colors.red.700)]",
        ],
        orange: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.orange.500)] [--btn-border:theme(colors.orange.600/90%)]",
          "[--btn-icon:theme(colors.orange.300)] data-[active]:[--btn-icon:theme(colors.orange.200)] data-[hover]:[--btn-icon:theme(colors.orange.200)]",
        ],
        "orange/soft": [
          "text-orange-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.orange.100)]",
          "[--btn-icon:theme(colors.orange.600)] data-[active]:[--btn-icon:theme(colors.orange.700)] data-[hover]:[--btn-icon:theme(colors.orange.700)]",
        ],
        amber: [
          "text-amber-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.amber.400)] [--btn-border:theme(colors.amber.500/80%)]",
          "[--btn-icon:theme(colors.amber.600)]",
        ],
        "amber/soft": [
          "text-amber-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.amber.100)]",
          "[--btn-icon:theme(colors.amber.600)] data-[active]:[--btn-icon:theme(colors.amber.700)] data-[hover]:[--btn-icon:theme(colors.amber.700)]",
        ],
        yellow: [
          "text-yellow-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.yellow.300)] [--btn-border:theme(colors.yellow.400/80%)]",
          "[--btn-icon:theme(colors.yellow.600)] data-[active]:[--btn-icon:theme(colors.yellow.700)] data-[hover]:[--btn-icon:theme(colors.yellow.700)]",
        ],
        "yellow/soft": [
          "text-yellow-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.yellow.100)]",
          "[--btn-icon:theme(colors.yellow.600)] data-[active]:[--btn-icon:theme(colors.yellow.700)] data-[hover]:[--btn-icon:theme(colors.yellow.700)]",
        ],
        lime: [
          "text-lime-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.lime.300)] [--btn-border:theme(colors.lime.400/80%)]",
          "[--btn-icon:theme(colors.lime.600)] data-[active]:[--btn-icon:theme(colors.lime.700)] data-[hover]:[--btn-icon:theme(colors.lime.700)]",
        ],
        "lime/soft": [
          "text-lime-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.lime.100)]",
          "[--btn-icon:theme(colors.lime.600)] data-[active]:[--btn-icon:theme(colors.lime.700)] data-[hover]:[--btn-icon:theme(colors.lime.700)]",
        ],
        green: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.green.600)] [--btn-border:theme(colors.green.700/90%)]",
          "[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]",
        ],
        "green/soft": [
          "text-green-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.green.100)]",
          "[--btn-icon:theme(colors.green.600)] data-[active]:[--btn-icon:theme(colors.green.700)] data-[hover]:[--btn-icon:theme(colors.green.700)]",
        ],
        emerald: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.emerald.600)] [--btn-border:theme(colors.emerald.700/90%)]",
          "[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]",
        ],
        "emerald/soft": [
          "text-emerald-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.emerald.100)]",
          "[--btn-icon:theme(colors.emerald.600)] data-[active]:[--btn-icon:theme(colors.emerald.700)] data-[hover]:[--btn-icon:theme(colors.emerald.700)]",
        ],
        teal: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.teal.600)] [--btn-border:theme(colors.teal.700/90%)]",
          "[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]",
        ],
        "teal/soft": [
          "text-teal-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.teal.100)]",
          "[--btn-icon:theme(colors.teal.600)] data-[active]:[--btn-icon:theme(colors.teal.700)] data-[hover]:[--btn-icon:theme(colors.teal.700)]",
        ],
        sky: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.sky.500)] [--btn-border:theme(colors.sky.600/80%)]",
          "[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]",
        ],
        "sky/soft": [
          "text-sky-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.sky.100)]",
          "[--btn-icon:theme(colors.sky.600)] data-[active]:[--btn-icon:theme(colors.sky.700)] data-[hover]:[--btn-icon:theme(colors.sky.700)]",
        ],
        blue: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.blue.600)] [--btn-border:theme(colors.blue.700/90%)]",
          "[--btn-icon:theme(colors.blue.400)] data-[active]:[--btn-icon:theme(colors.blue.300)] data-[hover]:[--btn-icon:theme(colors.blue.300)]",
        ],
        "blue/soft": [
          "text-blue-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.blue.100)]",
          "[--btn-icon:theme(colors.blue.600)] data-[active]:[--btn-icon:theme(colors.blue.700)] data-[hover]:[--btn-icon:theme(colors.blue.700)]",
        ],
        violet: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.violet.500)] [--btn-border:theme(colors.violet.600/90%)]",
          "[--btn-icon:theme(colors.violet.300)] data-[active]:[--btn-icon:theme(colors.violet.200)] data-[hover]:[--btn-icon:theme(colors.violet.200)]",
        ],
        "violet/soft": [
          "text-violet-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.violet.100)]",
          "[--btn-icon:theme(colors.violet.600)] data-[active]:[--btn-icon:theme(colors.violet.700)] data-[hover]:[--btn-icon:theme(colors.violet.700)]",
        ],
        purple: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.purple.500)] [--btn-border:theme(colors.purple.600/90%)]",
          "[--btn-icon:theme(colors.purple.300)] data-[active]:[--btn-icon:theme(colors.purple.200)] data-[hover]:[--btn-icon:theme(colors.purple.200)]",
        ],
        "purple/soft": [
          "text-purple-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.purple.100)]",
          "[--btn-icon:theme(colors.purple.600)] data-[active]:[--btn-icon:theme(colors.purple.700)] data-[hover]:[--btn-icon:theme(colors.purple.700)]",
        ],
        fuchsia: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.fuchsia.500)] [--btn-border:theme(colors.fuchsia.600/90%)]",
          "[--btn-icon:theme(colors.fuchsia.300)] data-[active]:[--btn-icon:theme(colors.fuchsia.200)] data-[hover]:[--btn-icon:theme(colors.fuchsia.200)]",
        ],
        "fuchsia/soft": [
          "text-fuchsia-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.fuchsia.100)]",
          "[--btn-icon:theme(colors.fuchsia.600)] data-[active]:[--btn-icon:theme(colors.fuchsia.700)] data-[hover]:[--btn-icon:theme(colors.fuchsia.700)]",
        ],
        pink: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.pink.500)] [--btn-border:theme(colors.pink.600/90%)]",
          "[--btn-icon:theme(colors.pink.300)] data-[active]:[--btn-icon:theme(colors.pink.200)] data-[hover]:[--btn-icon:theme(colors.pink.200)]",
        ],
        "pink/soft": [
          "text-pink-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.pink.100)]",
          "[--btn-icon:theme(colors.pink.600)] data-[active]:[--btn-icon:theme(colors.pink.700)] data-[hover]:[--btn-icon:theme(colors.pink.700)]",
        ],
        rose: [
          "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.rose.500)] [--btn-border:theme(colors.rose.600/90%)]",
          "[--btn-icon:theme(colors.rose.300)] data-[active]:[--btn-icon:theme(colors.rose.200)] data-[hover]:[--btn-icon:theme(colors.rose.200)]",
        ],
        "rose/soft": [
          "text-rose-700 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.rose.100)]",
          "[--btn-icon:theme(colors.rose.600)] data-[active]:[--btn-icon:theme(colors.rose.700)] data-[hover]:[--btn-icon:theme(colors.rose.700)]",
        ],
      },
      icon: {
        true: "",
        false: "",
        sm: "",
        md: "",
        lg: "",
        xl: "",
        "2xl": "",
      },
      size: {
        "2xs": [
          "[--border-radius:theme(borderRadius.md)]",
          "text-xs/4 [&>[data-slot=loader]]:size-4 [&>[data-slot=loader]]:sm:size-3",
          "gap-x-md px-lg h-6",
        ],
        xs: [
          "[--border-radius:theme(borderRadius.md)]",
          "text-xs/4",
          "gap-x-md px-lg h-7",
        ],
        sm: [
          "[--border-radius:theme(borderRadius.lg)]",
          "text-sm/5",
          "gap-x-xl px-xl h-8",
        ],
        md: [
          "[--border-radius:theme(borderRadius.lg)]",
          "text-sm/5",
          "gap-x-2xl px-2xl h-9",
        ],
        lg: [
          "[--border-radius:theme(borderRadius.lg)]",
          "text-base/6",
          "gap-x-4xl px-4xl h-10",
        ],
        xl: [
          "[--border-radius:theme(borderRadius.lg)]",
          "text-base/6",
          "gap-x-5 px-5 h-12",
        ],
      },
      variant: {
        primary: [
          // Optical border, implemented as the button background to avoid corner artifacts
          "border-transparent bg-[--btn-border]",

          // Dark mode: border is rendered on `after` so background is set to button background
          "dark:bg-[--btn-bg]",

          // Button background, implemented as foreground layer to stack on top of pseudo-border layer
          "before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--border-radius)-1px)] before:bg-[--btn-bg]",

          // Drop shadow, applied to the inset `before` layer so it blends with the border
          "before:shadow",

          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          "dark:before:hidden",

          // Dark mode: Subtle white outline is applied using a border
          "dark:border-white/5",

          // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
          "after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--border-radius)-1px)] after:transition-colors",

          // Inner highlight shadow
          "after:shadow-[shadow:inset_0_1px_theme(colors.white/15%)]",

          // White overlay on hover
          "after:data-[active]:bg-[--btn-hover-overlay] after:data-[hover]:bg-[--btn-hover-overlay]",

          // Dark mode: `after` layer expands to cover entire button
          "dark:after:-inset-px dark:after:rounded-lg",

          // Disabled
          "before:data-[disabled]:shadow-none after:data-[disabled]:shadow-none",
        ],
        secondary: [
          // Base
          "border-zinc-950/10 text-zinc-950 data-[active]:bg-zinc-950/[2.5%] data-[hover]:bg-zinc-950/[2.5%]",

          // Dark mode
          "dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-[active]:bg-white/5 dark:data-[hover]:bg-white/5",

          // Icon
          "[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]",
        ],
        tertiary: [
          // Base
          "border-transparent text-zinc-950 bg-zinc-950/[2.5%] data-[active]:bg-zinc-950/5 data-[hover]:bg-zinc-950/5",

          // Dark mode
          "dark:border-transparent dark:text-white dark:bg-white/10 dark:data-[active]:bg-white/20 dark:data-[hover]:bg-white/20",

          // Icon
          "[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]",
        ],
        ghost: [
          // Base
          "border-transparent text-zinc-950 data-[active]:bg-zinc-950/[2.5%] data-[hover]:bg-zinc-950/[2.5%]",

          // Dark mode
          "dark:border-transparent dark:text-white dark:[--btn-bg:transparent] dark:data-[active]:bg-white/5 dark:data-[hover]:bg-white/5",

          // Icon
          "[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]",
        ],
        inverse: [
          // Border
          "border-transparent",
          // Colors
          "bg-white !text-black !dark:text-black",
          // Hover
          "data-[hover]:bg-gradient-to-r from-overlay to-overlay",
        ],
      },
    },
    compoundVariants: [
      { size: "2xs", icon: true, className: "size-6" },
      { size: "xs", icon: true, className: "size-7" },
      { size: "sm", icon: true, className: "size-8" },
      { size: "md", icon: true, className: "size-9" },
      { size: "lg", icon: true, className: "size-10" },
      { size: "xl", icon: true, className: "size-12" },
      { size: "2xs", icon: "sm", className: "max-sm:size-6" },
      { size: "xs", icon: "sm", className: "max-sm:size-7" },
      { size: "sm", icon: "sm", className: "max-sm:size-8" },
      { size: "md", icon: "sm", className: "max-sm:size-9" },
      { size: "lg", icon: "sm", className: "max-sm:size-10" },
      { size: "xl", icon: "sm", className: "max-sm:size-12" },
      { size: "2xs", icon: "md", className: "max-md:size-6" },
      { size: "xs", icon: "md", className: "max-md:size-7" },
      { size: "sm", icon: "md", className: "max-md:size-8" },
      { size: "md", icon: "md", className: "max-md:size-9" },
      { size: "lg", icon: "md", className: "max-md:size-10" },
      { size: "xl", icon: "md", className: "max-md:size-12" },
      { size: "2xs", icon: "lg", className: "max-lg:size-6" },
      { size: "xs", icon: "lg", className: "max-lg:size-7" },
      { size: "sm", icon: "lg", className: "max-lg:size-8" },
      { size: "md", icon: "lg", className: "max-lg:size-9" },
      { size: "lg", icon: "lg", className: "max-lg:size-10" },
      { size: "xl", icon: "lg", className: "max-lg:size-12" },
      { size: "2xs", icon: "xl", className: "max-xl:size-6" },
      { size: "xs", icon: "xl", className: "max-xl:size-7" },
      { size: "sm", icon: "xl", className: "max-xl:size-8" },
      { size: "md", icon: "xl", className: "max-xl:size-9" },
      { size: "lg", icon: "xl", className: "max-xl:size-10" },
      { size: "xl", icon: "xl", className: "max-xl:size-12" },
      { size: "2xs", icon: "2xl", className: "max-2xl:size-6" },
      { size: "xs", icon: "2xl", className: "max-2xl:size-7" },
      { size: "sm", icon: "2xl", className: "max-2xl:size-8" },
      { size: "md", icon: "2xl", className: "max-2xl:size-9" },
      { size: "lg", icon: "2xl", className: "max-xl:size-10" },
      { size: "xl", icon: "2xl", className: "max-2xl:size-12" },
      ...removedShadowColors.reduce<
        Array<{
          variant: "primary";
          color: (typeof removedShadowColors)[number];
          className: string;
        }>
      >((acc, color) => {
        acc.push({
          variant: "primary",
          color,
          className: "before:shadow-none",
        });
        return acc;
      }, []),
    ],
  },
);
